import React from "react";
import { Card, Button, Typography, Row, Col, Statistic, Badge } from "antd";
import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  PhoneOutlined,
  MessageOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  UserOutlined,
  StarOutlined,
  TrophyOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { primaryColor } from "../../utils/constants";

const { Title, Text, Paragraph } = Typography;

export const BecomeConsultantLanding = () => {
  const navigate = useNavigate();

  const earningFeatures = [
    {
      title: "Paid Voice Calls",
      description:
        "Earn money for every minute you spend on voice calls with clients",
      icon: <PhoneOutlined style={{ fontSize: "24px", color: primaryColor }} />,
      highlight: "₹10-500/min",
      details: "Set your own per-minute rate based on your expertise",
    },
    {
      title: "Free Chat to Convert",
      description:
        "Use free chat to build rapport and convince clients to book paid calls",
      icon: <MessageOutlined style={{ fontSize: "24px", color: "#52c41a" }} />,
      highlight: "Free Chat",
      details: "Build trust through messaging, then convert to paid calls",
    },
    {
      title: "Flexible Pricing",
      description:
        "Set competitive rates based on your experience and specialization",
      icon: <DollarOutlined style={{ fontSize: "24px", color: "#faad14" }} />,
      highlight: "Your Rates",
      details: "Complete control over your pricing strategy",
    },
    {
      title: "Instant Payouts",
      description:
        "Get paid immediately after each successful consultation call",
      icon: <TrophyOutlined style={{ fontSize: "24px", color: "#722ed1" }} />,
      highlight: "Instant Pay",
      details: "No waiting periods - earn and withdraw instantly",
    },
  ];

  const platformStats = [
    { title: "Active Users", value: "50K+", icon: <UserOutlined /> },
    {
      title: "Avg. Earnings",
      value: "₹25K",
      suffix: "/month",
      icon: <DollarOutlined />,
    },
    { title: "Success Rate", value: "94%", icon: <StarOutlined /> },
    {
      title: "Avg. Rating",
      value: "4.8",
      suffix: "/5",
      icon: <TrophyOutlined />,
    },
  ];

  const howItWorks = [
    {
      step: 1,
      title: "Create Your Profile",
      description:
        "Set up your consultant profile with expertise, rates, and availability",
      icon: <UserOutlined />,
      color: "#1890ff",
    },
    {
      step: 2,
      title: "Chat with Clients",
      description:
        "Start free conversations to understand client needs and build trust",
      icon: <MessageOutlined />,
      color: "#52c41a",
    },
    {
      step: 3,
      title: "Convert to Paid Calls",
      description:
        "Convince clients to book paid voice calls for detailed consultation",
      icon: <PhoneOutlined />,
      color: primaryColor,
    },
    {
      step: 4,
      title: "Earn Money",
      description:
        "Get paid per minute for every consultation call you complete",
      icon: <DollarOutlined />,
      color: "#faad14",
    },
  ];

  const consultantTypes = [
    {
      category: "Relationship Counselor",
      rate: "₹100-300/min",
      description:
        "Help people with dating, relationships, and personal connections",
      demand: "High",
      icon: "💕",
    },
    {
      category: "Career Coach",
      rate: "₹150-400/min",
      description: "Guide professionals in career growth and job transitions",
      demand: "Very High",
      icon: "💼",
    },
    {
      category: "Life Coach",
      rate: "₹120-350/min",
      description: "Support individuals in personal development and life goals",
      demand: "High",
      icon: "🌟",
    },
    {
      category: "Business Advisor",
      rate: "₹200-500/min",
      description: "Provide strategic advice for entrepreneurs and businesses",
      demand: "Medium",
      icon: "📈",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-pink-600/10"></div>
        <div className="container mx-auto px-4 py-16 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <Badge.Ribbon
              text="Earn ₹25K+/Month"
              color={primaryColor}
              className="mb-4"
            >
              <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-2xl border border-white/20">
                <Title
                  level={1}
                  className="text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-6"
                >
                  Become a Consultant
                </Title>
                <Title
                  level={2}
                  style={{ color: primaryColor, marginBottom: "24px" }}
                >
                  Turn Your Expertise Into Income
                </Title>
                <Paragraph className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
                  Join thousands of consultants earning money by helping others
                  through <strong>paid voice calls</strong>. Start with{" "}
                  <strong>free chat</strong> to build trust, then convert
                  clients to <strong>paid consultations</strong>
                  at your own rates.
                </Paragraph>

                <div className="flex flex-wrap justify-center gap-6 mt-8 mb-10">
                  <div className="flex items-center bg-white/60 rounded-full px-6 py-3 shadow-lg">
                    <MessageOutlined
                      style={{ color: "#52c41a", marginRight: "8px" }}
                    />
                    <Text strong>Free Chat Available</Text>
                  </div>
                  <div className="flex items-center bg-white/60 rounded-full px-6 py-3 shadow-lg">
                    <PhoneOutlined
                      style={{ color: primaryColor, marginRight: "8px" }}
                    />
                    <Text strong>Paid Voice Calls</Text>
                  </div>
                  <div className="flex items-center bg-white/60 rounded-full px-6 py-3 shadow-lg">
                    <DollarOutlined
                      style={{ color: "#faad14", marginRight: "8px" }}
                    />
                    <Text strong>Instant Payouts</Text>
                  </div>
                </div>

                {/* Header CTA Button */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                  className="flex flex-col items-center gap-6"
                >
                  <Button
                    type="primary"
                    size="large"
                    icon={<ArrowRightOutlined />}
                    onClick={() => navigate("/become-consultant/form")}
                    className="px-16 py-4 h-16 text-xl font-bold rounded-2xl shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105"
                    style={{
                      backgroundColor: primaryColor,
                      borderColor: primaryColor,
                      background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}DD 100%)`,
                      boxShadow: `0 10px 30px ${primaryColor}40`,
                    }}
                  >
                    🚀 Start Earning Today - Join Now!
                  </Button>

                  <div className="flex flex-wrap justify-center gap-4 text-sm">
                    <div className="flex items-center bg-green-100 rounded-full px-4 py-2">
                      <CheckCircleOutlined
                        style={{
                          color: "#52c41a",
                          marginRight: "6px",
                          fontSize: "14px",
                        }}
                      />
                      <Text style={{ color: "#52c41a", fontWeight: "600" }}>
                        100% Free to Join
                      </Text>
                    </div>
                    <div className="flex items-center bg-blue-100 rounded-full px-4 py-2">
                      <ClockCircleOutlined
                        style={{
                          color: "#1890ff",
                          marginRight: "6px",
                          fontSize: "14px",
                        }}
                      />
                      <Text style={{ color: "#1890ff", fontWeight: "600" }}>
                        Quick 5-Min Setup
                      </Text>
                    </div>
                    <div className="flex items-center bg-yellow-100 rounded-full px-4 py-2">
                      <TrophyOutlined
                        style={{
                          color: "#faad14",
                          marginRight: "6px",
                          fontSize: "14px",
                        }}
                      />
                      <Text style={{ color: "#faad14", fontWeight: "600" }}>
                        Start Earning Today
                      </Text>
                    </div>
                  </div>

                  {/* Urgency Element */}
                  <motion.div
                    animate={{
                      scale: [1, 1.05, 1],
                      opacity: [0.8, 1, 0.8],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-6 py-2 rounded-full shadow-lg"
                  >
                    <Text
                      style={{
                        color: "white",
                        fontWeight: "bold",
                        fontSize: "14px",
                      }}
                    >
                      🔥 Limited Time: No Setup Fees - Join Free!
                    </Text>
                  </motion.div>
                </motion.div>
              </div>
            </Badge.Ribbon>
          </motion.div>

          {/* Platform Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-16"
          >
            <Row gutter={[24, 24]} justify="center">
              {platformStats.map((stat, index) => (
                <Col xs={12} sm={6} key={index}>
                  <Card
                    className="text-center rounded-2xl shadow-lg border-0 bg-white/80 backdrop-blur-sm hover:shadow-xl transition-all duration-300"
                    style={{ boxShadow: "0 10px 40px rgba(164, 1, 101, 0.1)" }}
                  >
                    <div
                      style={{
                        color: primaryColor,
                        fontSize: "24px",
                        marginBottom: "8px",
                      }}
                    >
                      {stat.icon}
                    </div>
                    <Statistic
                      title={stat.title}
                      value={stat.value}
                      suffix={stat.suffix}
                      valueStyle={{ color: primaryColor, fontWeight: "bold" }}
                    />
                  </Card>
                </Col>
              ))}
            </Row>
          </motion.div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-16">
        {/* Earning Features Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-16"
        >
          <div className="text-center mb-12">
            <Title level={2} style={{ color: primaryColor }}>
              💰 How You Earn Money
            </Title>
            <Paragraph className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our unique dual-channel approach helps you maximize earnings
              through strategic client conversion
            </Paragraph>
          </div>

          <Row gutter={[24, 24]}>
            {earningFeatures.map((feature, index) => (
              <Col xs={24} sm={12} lg={6} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                >
                  <Card
                    className="h-full rounded-2xl shadow-lg border-0 bg-white/90 backdrop-blur-sm hover:shadow-2xl transition-all duration-300 hover:scale-105"
                    style={{ boxShadow: "0 10px 40px rgba(164, 1, 101, 0.1)" }}
                  >
                    <div className="text-center mb-4">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 flex items-center justify-center mx-auto mb-4">
                        {feature.icon}
                      </div>
                      <Badge
                        count={feature.highlight}
                        style={{
                          backgroundColor: primaryColor,
                          fontSize: "12px",
                          fontWeight: "bold",
                        }}
                      />
                    </div>
                    <Title level={4} className="text-center mb-3">
                      {feature.title}
                    </Title>
                    <Paragraph className="text-center text-gray-600 mb-3">
                      {feature.description}
                    </Paragraph>
                    <div className="text-center">
                      <Text type="secondary" className="text-sm italic">
                        {feature.details}
                      </Text>
                    </div>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </motion.div>

        {/* How It Works Process */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="mb-16"
        >
          <div className="text-center mb-12">
            <Title level={2} style={{ color: primaryColor }}>
              🚀 Your Journey to Success
            </Title>
            <Paragraph className="text-lg text-gray-600 max-w-2xl mx-auto">
              From profile creation to earning money - here's how our platform
              works
            </Paragraph>
          </div>

          <Row gutter={[32, 32]} align="middle">
            {howItWorks.map((step, index) => (
              <Col xs={24} sm={12} lg={6} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 + index * 0.2 }}
                  className="text-center"
                >
                  <div
                    className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold shadow-lg"
                    style={{
                      backgroundColor: step.color,
                      boxShadow: `0 8px 25px ${step.color}40`,
                    }}
                  >
                    {step.icon}
                  </div>
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold"
                    style={{ backgroundColor: step.color }}
                  >
                    {step.step}
                  </div>
                  <Title level={4} className="mb-3">
                    {step.title}
                  </Title>
                  <Paragraph className="text-gray-600">
                    {step.description}
                  </Paragraph>
                </motion.div>
              </Col>
            ))}
          </Row>
        </motion.div>

        {/* Success Stories & Testimonials */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.9 }}
          className="mb-16"
        >
          <div className="text-center mb-12">
            <Title level={2} style={{ color: primaryColor }}>
              💬 What Our Consultants Say
            </Title>
          </div>

          <Row gutter={[24, 24]}>
            <Col xs={24} md={8}>
              <Card className="rounded-2xl shadow-lg border-0 bg-gradient-to-br from-blue-50 to-indigo-50 h-full">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-3">
                    <UserOutlined
                      style={{ fontSize: "24px", color: "#1890ff" }}
                    />
                  </div>
                  <Text strong>Priya S. - Relationship Coach</Text>
                </div>
                <Paragraph className="text-center italic text-gray-600">
                  "I earn ₹35K monthly by helping people with relationship
                  advice. The free chat feature helps me understand clients
                  better before they book paid calls."
                </Paragraph>
                <div className="text-center">
                  <Text strong style={{ color: "#1890ff" }}>
                    ₹35,000/month
                  </Text>
                </div>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card className="rounded-2xl shadow-lg border-0 bg-gradient-to-br from-green-50 to-emerald-50 h-full">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                    <TrophyOutlined
                      style={{ fontSize: "24px", color: "#52c41a" }}
                    />
                  </div>
                  <Text strong>Rahul M. - Career Coach</Text>
                </div>
                <Paragraph className="text-center italic text-gray-600">
                  "Started part-time, now earning ₹50K+ monthly. The platform's
                  chat-to-call conversion strategy really works!"
                </Paragraph>
                <div className="text-center">
                  <Text strong style={{ color: "#52c41a" }}>
                    ₹50,000+/month
                  </Text>
                </div>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card className="rounded-2xl shadow-lg border-0 bg-gradient-to-br from-purple-50 to-pink-50 h-full">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-3">
                    <StarOutlined
                      style={{ fontSize: "24px", color: primaryColor }}
                    />
                  </div>
                  <Text strong>Anjali K. - Life Coach</Text>
                </div>
                <Paragraph className="text-center italic text-gray-600">
                  "The instant payout feature is amazing. I get paid immediately
                  after each consultation call. Perfect for freelancers!"
                </Paragraph>
                <div className="text-center">
                  <Text strong style={{ color: primaryColor }}>
                    ₹28,000/month
                  </Text>
                </div>
              </Card>
            </Col>
          </Row>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          className="text-center"
        >
          <Card
            className="max-w-4xl mx-auto rounded-3xl shadow-2xl border-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white overflow-hidden"
            style={{ boxShadow: "0 25px 80px rgba(164, 1, 101, 0.3)" }}
          >
            <div className="py-12 px-8 relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600/90 to-pink-600/90"></div>
              <div className="relative z-10">
                <Title
                  level={2}
                  style={{ color: "white", marginBottom: "16px" }}
                >
                  🚀 Ready to Start Earning?
                </Title>
                <Paragraph className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                  Join <strong>50,000+</strong> consultants already earning
                  money through our platform. Start with free chat, convert to
                  paid calls, and build your consulting business today!
                </Paragraph>

                <div className="flex flex-wrap justify-center gap-4 mb-8">
                  <div className="flex items-center bg-white/20 rounded-full px-6 py-3">
                    <CheckCircleOutlined
                      style={{ color: "white", marginRight: "8px" }}
                    />
                    <Text style={{ color: "white" }}>Free to Join</Text>
                  </div>
                  <div className="flex items-center bg-white/20 rounded-full px-6 py-3">
                    <ClockCircleOutlined
                      style={{ color: "white", marginRight: "8px" }}
                    />
                    <Text style={{ color: "white" }}>Quick Approval</Text>
                  </div>
                  <div className="flex items-center bg-white/20 rounded-full px-6 py-3">
                    <SafetyOutlined
                      style={{ color: "white", marginRight: "8px" }}
                    />
                    <Text style={{ color: "white" }}>Secure Platform</Text>
                  </div>
                </div>

                <Button
                  type="primary"
                  size="large"
                  icon={<ArrowRightOutlined />}
                  onClick={() => navigate("/become-consultant/form")}
                  className="px-16 py-4 h-16 text-xl font-bold rounded-2xl shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105 bg-white text-purple-600 border-0 hover:bg-gray-50"
                >
                  Start Your Consultant Journey
                </Button>

                <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-2xl mx-auto">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-white">₹25K+</div>
                    <div className="text-white/80 text-sm">
                      Average Monthly Earnings
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-white">94%</div>
                    <div className="text-white/80 text-sm">Success Rate</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-white">24/7</div>
                    <div className="text-white/80 text-sm">
                      Platform Support
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Footer Note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.2 }}
          className="text-center mt-16"
        >
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 max-w-md mx-auto">
            <Text type="secondary" className="text-sm">
              💡 <strong>Pro Tip:</strong> Start with competitive rates to build
              your client base, then gradually increase as you gain reviews and
              experience.
            </Text>
          </div>
        </motion.div>
      </div>
    </div>
  );
};
